import { createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { SourceDetail } from "api/schemas/sourcesApiSchemas";
import { getCanAccessCreate, getCanSeeSectionContent } from "entities/permissions/selectors";
import type { RootState } from "types/redux";

type SearchState = {
  currentSearchQuery: string;
  sources: SourceDetail[];
  searchResultsVisibility: {
    areTopicsVisible: boolean;
    areKeyIssuesVisible: boolean;
    arePublicationsVisible: boolean;
    areVideosVisible: boolean;
    isInteractiveDataVisible: boolean;
  };
};

const initialState: SearchState = {
  currentSearchQuery: "",
  sources: [],
  searchResultsVisibility: {
    areTopicsVisible: true,
    areKeyIssuesVisible: true,
    arePublicationsVisible: true,
    areVideosVisible: true,
    isInteractiveDataVisible: true,
  },
};

export const searchSlice = createSlice({
  initialState,
  name: "search",
  reducers: {
    updateSearchQuery: (state, action: PayloadAction<{ query: string }>) => {
      state.currentSearchQuery = action.payload.query;
    },
    clearSearchQuery: (state) => {
      state.currentSearchQuery = "";
    },
    addSource: (state, action: PayloadAction<{ source: SourceDetail }>) => {
      if (state.sources.find((source) => action.payload.source.id === source.id)) return;
      state.sources.push(action.payload.source);
    },
    removeSource: (state, action: PayloadAction<{ removedSource: SourceDetail }>) => {
      const index = state.sources.findIndex((source) => source.id === action.payload.removedSource.id);
      if (index !== -1) state.sources.splice(index, 1);
    },
    setTopicsVisibility: (state, action: PayloadAction<{ isVisible: boolean }>) => {
      state.searchResultsVisibility.areTopicsVisible = action.payload.isVisible;
    },
    setKeyIssuesVisibility: (state, action: PayloadAction<{ isVisible: boolean }>) => {
      state.searchResultsVisibility.areKeyIssuesVisible = action.payload.isVisible;
    },
    setPublicationsVisibility: (state, action: PayloadAction<{ isVisible: boolean }>) => {
      state.searchResultsVisibility.arePublicationsVisible = action.payload.isVisible;
    },
    setVideosVisibility: (state, action: PayloadAction<{ isVisible: boolean }>) => {
      state.searchResultsVisibility.areVideosVisible = action.payload.isVisible;
    },
    setInteractiveDataVisibility: (state, action: PayloadAction<{ isVisible: boolean }>) => {
      state.searchResultsVisibility.isInteractiveDataVisible = action.payload.isVisible;
    },
  },
  selectors: {
    getCurrentSearchQuery: (state) => state.currentSearchQuery,
    getSearchResultsVisibility: (state) => state.searchResultsVisibility,
  },
});

export const getEffectiveSearchResultsVisibility = createSelector(
  [
    (state: RootState) => searchSlice.selectors.getSearchResultsVisibility(state),
    getCanAccessCreate,
    getCanSeeSectionContent,
  ],
  (visibility, canAccessCreate, canSeeSectionContent) => ({
    areTopicsVisible: visibility.areTopicsVisible,
    areKeyIssuesVisible: visibility.areKeyIssuesVisible && canAccessCreate,
    arePublicationsVisible: visibility.arePublicationsVisible && canSeeSectionContent,
    areVideosVisible: visibility.areVideosVisible && canSeeSectionContent,
    isInteractiveDataVisible: visibility.isInteractiveDataVisible && canSeeSectionContent,
  }),
);
